/**
 * `/admin/*` gate — editor-only tools (image audit, 404 analytics).
 *
 * For a non-editor, don't render the inline "editors only" state: bounce to the
 * locale home with a toast. The authoritative boundary stays server-side
 * (`requireCmsEditor` on every `/api/cms/*` + `/api/monitor/*` endpoint) — this
 * is only the UX redirect, so it's client-side (SPA nav + hydration), mirroring
 * `auth.global.ts`. Uses the non-throwing `/api/cms/session` probe (`canEdit`).
 */
export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) return;
  if (!/^\/admin(?:\/|$)/.test(to.path)) return;

  let canEdit = false;
  try {
    const s = await $fetch<{ canEdit?: boolean }>("/api/cms/session");
    canEdit = !!s?.canEdit;
  } catch {
    canEdit = false;
  }
  if (canEdit) return;

  document.dispatchEvent(
    new CustomEvent("e-toast:create", {
      detail: {
        id: `admin-forbidden-${Date.now()}`,
        type: "warning",
        title: "You don't have permission to visit that page",
      },
    }),
  );

  const defaultLocale =
    (useRuntimeConfig().public.defaultLocale as string) || "en";
  return navigateTo(`/${defaultLocale}`, { replace: true });
});
